import axios, { AxiosInstance } from 'axios';

// Determine API base URL. Always ends in /api - callers add the path after it
// (e.g. `${getApiBaseUrl()}/auth/verify`), never another /api.
export const getApiBaseUrl = () => {
  // If explicitly set, use it as-is (it should already include /api)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // For local development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api';
  }

  // For production, construct from current protocol and hostname
  // Assumes backend is on same domain or you need to set VITE_API_URL
  const protocol = window.location.protocol;
  return `${protocol}//${window.location.hostname}/api`;
};

const API_BASE_URL = getApiBaseUrl();

console.log('[ApiService] API_BASE_URL:', API_BASE_URL);
console.log('[ApiService] VITE_API_URL:', import.meta.env.VITE_API_URL);
console.log('[ApiService] window.location:', window.location.href);
console.log('[ApiService] hostname:', window.location.hostname);

class ApiService {
  public client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // A 401 usually just means the access token expired (after 15 minutes).
    // Renew it with the refresh token and retry once; only sign out if that
    // fails. This used to sign out on the first 401, so any click or reload
    // 15 minutes after logging in went back to the login page.
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const isTokenCall = /\/auth\/(login|refresh|logout)/.test(original?.url || '');
        if (error.response?.status === 401 && original && !original._retried && !isTokenCall) {
          original._retried = true;
          try {
            const token = await this.renewToken();
            original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
            return this.client(original);
          } catch {
            // fall through to signing out
          }
        }
        if (error.response?.status === 401 && !isTokenCall) {
          // Back to the sign-in for the same company (portal.bomizzel.com/<company>/login)
          let company = '';
          try {
            const c = JSON.parse(localStorage.getItem('company') || 'null');
            if (/^portal\./i.test(window.location.hostname) && c?.portalName) {
              company = `/${c.portalName}`;
            }
          } catch {
            // no company kept
          }
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          window.location.href = window.location.pathname.startsWith('/bsi')
            ? '/bsi/login'
            : `${company}/login`;
        }
        return Promise.reject(error);
      }
    );
  }

  // One renewal at a time: refresh tokens are single-use, so two requests
  // failing together must share the same renewal.
  private renewing: Promise<string> | null = null;

  private renewToken(): Promise<string> {
    if (!this.renewing) {
      const refreshToken = localStorage.getItem('refreshToken');
      this.renewing = (async () => {
        if (!refreshToken) throw new Error('No refresh token');
        const response = await axios.post(`${this.client.defaults.baseURL}/auth/refresh`, {
          refreshToken,
        });
        const { token, refreshToken: next } = response.data;
        if (!token) throw new Error('No token in refresh response');
        localStorage.setItem('token', token);
        if (next) localStorage.setItem('refreshToken', next);
        return token as string;
      })().finally(() => {
        this.renewing = null;
      });
    }
    return this.renewing;
  }

  /** Is the saved login still good? (Renews it if it has only expired.) */
  async verifySession(): Promise<any> {
    const response = await this.client.get('/auth/verify');
    return response.data;
  }

  // Auth endpoints
  // `to`: sign in to one company (its portal name), as staff or on its customer portal
  async login(
    email: string,
    password: string,
    to?: { company: string; audience: 'staff' | 'portal' }
  ): Promise<any> {
    try {
      console.log('[ApiService] Attempting login with baseURL:', this.client.defaults.baseURL);

      // Create a separate axios instance without auth interceptors for login
      const loginClient = axios.create({
        baseURL: this.client.defaults.baseURL,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await loginClient.post('/auth/login', { email, password, ...to });
      console.log('[ApiService] Login successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[ApiService] Login error:', error);
      console.error('[ApiService] Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
    selectedPlanId?: string;
    startTrial?: boolean;
  }): Promise<any> {
    const response = await this.client.post('/auth/register', userData);
    return response.data;
  }

  // Tokens are credentials until they expire, so logging out has to tell the
  // server to revoke them - clearing localStorage only hides them from this tab.
  // Both are passed explicitly: the caller clears storage immediately after, and
  // the request interceptor would otherwise read it after it was already empty.
  async logout(token?: string | null, refreshToken?: string | null): Promise<void> {
    await this.client.post(
      '/auth/logout',
      refreshToken ? { refreshToken } : {},
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
  }

  async refreshToken(refreshToken: string): Promise<any> {
    const response = await this.client.post('/auth/refresh', { refreshToken });
    return response.data;
  }

  /** A one-time link to this sign-in on the company's own address. */
  async handoff(): Promise<{ url: string }> {
    const response = await this.client.post('/auth/handoff');
    return response.data;
  }

  async getProfile(): Promise<any> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  async updateProfile(profileData: any): Promise<any> {
    const response = await this.client.put('/auth/profile', profileData);
    return response.data;
  }

  async changePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<any> {
    const response = await this.client.put('/auth/change-password', passwordData);
    return response.data;
  }

  async uploadProfilePicture(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await this.client.post('/auth/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // User search endpoints
  async searchUsers(query: string, params?: { limit?: number; role?: string }): Promise<any> {
    const response = await this.client.get('/users/search', {
      params: { q: query, ...params },
    });
    return response.data;
  }

  async listUsers(params?: { role?: string; limit?: number }): Promise<any> {
    // Fallback to admin endpoint if /users/list doesn't exist
    try {
      const response = await this.client.get('/users/list', { params });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Fallback to admin endpoint with proper pagination
        const response = await this.client.get('/admin/users', {
          params: {
            ...params,
            page: 1,
            limit: params?.limit || 1000,
          },
        });
        return response.data;
      }
      throw error;
    }
  }

  // Ticket endpoints
  async getTickets(params?: {
    companyId?: string;
    assignedToId?: string;
    departmentId?: number;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.client.get('/tickets', { params });
    return response.data;
  }

  // Email confirmation and password reset. Each answers without saying whether
  // an address has an account.
  async verifyEmail(token: string): Promise<any> {
    const response = await this.client.post('/auth/verify-email', { token });
    return response.data;
  }

  async resendVerification(email: string): Promise<any> {
    const response = await this.client.post('/auth/resend-verification', { email });
    return response.data;
  }

  async forgotPassword(email: string): Promise<any> {
    const response = await this.client.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<any> {
    const response = await this.client.post('/auth/reset-password', { token, password });
    return response.data;
  }

  async getTicket(ticketId: string): Promise<any> {
    const response = await this.client.get(`/tickets/${ticketId}`);
    return response.data;
  }

  async createTicket(ticketData: {
    title: string;
    description: string;
    companyId: string;
    teamId: string;
    customFieldValues?: Record<string, any>;
    submitterId?: string;
    departmentId?: number;
  }): Promise<any> {
    const response = await this.client.post('/tickets', ticketData);
    return response.data;
  }

  /** Save one board lane's order, top to bottom (ticket ids). */
  async updateBoardOrder(ticketIds: string[]): Promise<any> {
    const response = await this.client.put('/tickets/board-order', { ticketIds });
    return response.data;
  }

  async updateTicket(ticketId: string, updates: any): Promise<any> {
    console.log('[API] updateTicket called:', { ticketId, updates });
    const response = await this.client.put(`/tickets/${ticketId}`, updates);
    console.log('[API] updateTicket response:', response.data);
    return response.data;
  }

  // Ticket notes endpoints
  async getTicketNotes(
    ticketId: string,
    params?: {
      includeInternal?: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    const response = await this.client.get(`/tickets/${ticketId}/notes`, { params });
    return response.data;
  }

  async createTicketNote(
    ticketId: string,
    noteData: {
      content: string;
      contentHtml?: string;
      isInternal?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.post(`/tickets/${ticketId}/notes`, noteData);
    return response.data;
  }

  async updateNote(
    noteId: string,
    updates: {
      content?: string;
      contentHtml?: string;
      isInternal?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.put(`/notes/${noteId}`, updates);
    return response.data;
  }

  async deleteNote(noteId: string): Promise<any> {
    const response = await this.client.delete(`/notes/${noteId}`);
    return response.data;
  }

  // File attachment endpoints
  async uploadFile(ticketId: string, file: File, noteId?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (noteId) {
      formData.append('noteId', noteId);
    }

    const response = await this.client.post(`/tickets/${ticketId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getTicketAttachments(ticketId: string): Promise<any> {
    const response = await this.client.get(`/tickets/${ticketId}/attachments`);
    return response.data;
  }

  async downloadFile(fileId: string): Promise<Blob> {
    // GET /files/:id serves the file. This asked for /files/:id/download,
    // which doesn't exist, so every download failed.
    const response = await this.client.get(`/files/${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteFile(fileId: string): Promise<any> {
    const response = await this.client.delete(`/files/${fileId}`);
    return response.data;
  }

  // Company endpoints
  async getCompanies(params?: { search?: string; page?: number; limit?: number }): Promise<any> {
    const response = await this.client.get('/companies', { params });
    return response.data;
  }

  async getCompany(companyId: string): Promise<any> {
    const response = await this.client.get(`/companies/${companyId}`);
    return response.data;
  }

  async searchCompanies(query: string, params?: { limit?: number }): Promise<any> {
    const response = await this.client.get('/companies', {
      params: { search: query, ...params },
    });
    return response.data;
  }

  async createCompany(companyData: {
    name: string;
    domain?: string;
    primaryContact?: string;
    primaryEmail?: string;
    primaryPhone?: string;
  }): Promise<any> {
    const response = await this.client.post('/companies', companyData);
    return response.data;
  }

  async updateCompany(
    companyId: string,
    companyData: {
      name?: string;
      domain?: string;
      primaryContact?: string;
      primaryEmail?: string;
      primaryPhone?: string;
    }
  ): Promise<any> {
    const response = await this.client.put(`/companies/${companyId}`, companyData);
    return response.data;
  }

  async addUserToCompany(userId: string, companyId: string, role: string): Promise<any> {
    const response = await this.client.post(`/companies/${companyId}/users`, {
      userId,
      role,
    });
    return response.data;
  }

  async sendCustomerInvitation(userId: string): Promise<any> {
    const response = await this.client.post(`/users/${userId}/send-invitation`);
    return response.data;
  }

  // Team endpoints
  async getTeams(params?: { search?: string; page?: number; limit?: number }): Promise<any> {
    const response = await this.client.get('/teams', { params });
    return response.data;
  }

  async getTeam(teamId: string): Promise<any> {
    const response = await this.client.get(`/teams/${teamId}`);
    return response.data;
  }

  async createTeam(teamData: { name: string; description?: string }): Promise<any> {
    const response = await this.client.post('/teams', teamData);
    return response.data;
  }

  async updateTeam(
    teamId: string,
    updates: { name?: string; description?: string; isActive?: boolean }
  ): Promise<any> {
    const response = await this.client.put(`/teams/${teamId}`, updates);
    return response.data;
  }

  async getTeamMembers(teamId: string): Promise<any> {
    const response = await this.client.get(`/teams/${teamId}/members`);
    return response.data;
  }

  async addTeamMember(teamId: string, userId: string, role: string = 'member'): Promise<any> {
    const response = await this.client.post(`/teams/${teamId}/members`, { userId, role });
    return response.data;
  }

  async removeTeamMember(teamId: string, userId: string): Promise<any> {
    const response = await this.client.delete(`/teams/${teamId}/members/${userId}`);
    return response.data;
  }

  async updateTeamMemberRole(teamId: string, userId: string, role: string): Promise<any> {
    const response = await this.client.put(`/teams/${teamId}/members/${userId}/role`, { role });
    return response.data;
  }

  // Custom fields endpoints
  async getTeamCustomFields(teamId: string): Promise<any> {
    const response = await this.client.get(`/custom-fields/teams/${teamId}`);
    return response.data;
  }

  // These endpoints are team-scoped on the server, which is how it authorises
  // the caller against that team. The paths here omitted the team, so create,
  // update and delete all 404'd. teamId also cannot ride in the body: the Joi
  // schema rejects unknown keys.
  async createCustomField(customFieldData: {
    teamId: string;
    name: string;
    label: string;
    type: string;
    isRequired: boolean;
    options?: string[];
    validation?: any;
  }): Promise<any> {
    const { teamId, ...fieldData } = customFieldData;
    const response = await this.client.post(`/custom-fields/teams/${teamId}`, fieldData);
    return response.data;
  }

  async updateCustomField(teamId: string, fieldId: string, updates: any): Promise<any> {
    const response = await this.client.put(`/custom-fields/${fieldId}/teams/${teamId}`, updates);
    return response.data;
  }

  async deleteCustomField(teamId: string, fieldId: string): Promise<any> {
    const response = await this.client.delete(`/custom-fields/${fieldId}/teams/${teamId}`);
    return response.data;
  }

  async validateCustomFieldValues(teamId: string, values: Record<string, any>): Promise<any> {
    const response = await this.client.post(`/custom-fields/teams/${teamId}/validate`, { values });
    return response.data;
  }

  // Queue endpoints
  async getQueues(params?: {
    teamId?: string;
    type?: string;
    assignedToId?: string;
  }): Promise<any> {
    const response = await this.client.get('/queues', { params });
    return response.data;
  }

  async getQueue(queueId: string): Promise<any> {
    const response = await this.client.get(`/queues/${queueId}`);
    return response.data;
  }

  async getQueueTickets(
    queueId: string,
    params?: {
      status?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    const response = await this.client.get(`/queues/${queueId}/tickets`, { params });
    return response.data;
  }

  async getQueueMetrics(queueId: string): Promise<any> {
    const response = await this.client.get(`/queues/${queueId}/metrics`);
    return response.data;
  }

  // Ticket assignment and status endpoints
  async assignTicket(ticketId: string, assignedToId: string): Promise<any> {
    const response = await this.client.post(`/tickets/${ticketId}/assign`, { assignedToId });
    return response.data;
  }

  async unassignTicket(ticketId: string): Promise<any> {
    const response = await this.client.post(`/tickets/${ticketId}/unassign`);
    return response.data;
  }

  async updateTicketStatus(ticketId: string, status: string): Promise<any> {
    const response = await this.client.put(`/tickets/${ticketId}/status`, { status });
    return response.data;
  }

  async updateTicketPriority(ticketId: string, priority: number): Promise<any> {
    const response = await this.client.put(`/tickets/${ticketId}/priority`, { priority });
    return response.data;
  }

  // Agent management endpoints
  async getAgents(params?: { status?: string; teamId?: string }): Promise<any> {
    const response = await this.client.get('/agents', { params });
    return response.data;
  }

  // User preferences endpoints
  async updateUserPreferences(preferences: any): Promise<any> {
    const response = await this.client.put('/auth/preferences', preferences);
    return response.data;
  }

  // Team status endpoints
  async getTeamStatuses(teamId: string): Promise<any> {
    const response = await this.client.get(`/teams/${teamId}/statuses`);
    return response.data;
  }

  async createTeamStatus(
    teamId: string,
    statusData: {
      name: string;
      label: string;
      color: string;
      order: number;
      isDefault?: boolean;
      isClosed?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.post(`/teams/${teamId}/statuses`, statusData);
    return response.data;
  }

  async updateTeamStatus(teamId: string, statusId: string, updates: any): Promise<any> {
    const response = await this.client.put(`/teams/${teamId}/statuses/${statusId}`, updates);
    return response.data;
  }

  async deleteTeamStatus(teamId: string, statusId: string): Promise<any> {
    const response = await this.client.delete(`/teams/${teamId}/statuses/${statusId}`);
    return response.data;
  }

  // Bulk operations endpoints
  async bulkAssignTickets(ticketIds: string[], assignedToId: string): Promise<any> {
    const response = await this.client.post('/bulk/assign', { ticketIds, assignedToId });
    return response.data;
  }

  async bulkUpdateStatus(ticketIds: string[], status: string): Promise<any> {
    const response = await this.client.post('/bulk/status', { ticketIds, status });
    return response.data;
  }

  async bulkUpdatePriority(ticketIds: string[], priority: number): Promise<any> {
    const response = await this.client.post('/bulk/priority', { ticketIds, priority });
    return response.data;
  }

  async bulkMoveTickets(ticketIds: string[], queueId: string): Promise<any> {
    const response = await this.client.post('/bulk/move', { ticketIds, queueId });
    return response.data;
  }

  async bulkDeleteTickets(ticketIds: string[]): Promise<any> {
    const response = await this.client.delete('/bulk/delete', { data: { ticketIds } });
    return response.data;
  }

  async deleteTicket(ticketId: string): Promise<any> {
    const response = await this.client.delete(`/tickets/${ticketId}`);
    return response.data;
  }

  async validateTicketAccess(ticketIds: string[]): Promise<any> {
    const response = await this.client.post('/bulk/validate-access', { ticketIds });
    return response.data;
  }

  // Search endpoints
  async getSearchableFields(teamId: string): Promise<any> {
    const response = await this.client.get(`/search/fields/${teamId}`);
    return response.data;
  }

  async advancedSearch(searchRequest: any): Promise<any> {
    const response = await this.client.post('/search/tickets', searchRequest);
    return response.data;
  }

  async saveSearch(name: string, searchRequest: any): Promise<any> {
    const response = await this.client.post('/search/save', { name, searchRequest });
    return response.data;
  }

  async getSavedSearches(): Promise<any> {
    const response = await this.client.get('/search/saved');
    return response.data;
  }

  // Ticket history endpoint
  async getTicketHistory(ticketId: string): Promise<any> {
    const response = await this.client.get(`/tickets/${ticketId}/history`);
    return response.data;
  }

  // Subscription endpoints
  // Saved views of the ticket board
  async getViews(): Promise<{ views: any[]; canShare: boolean }> {
    const response = await this.client.get('/views');
    return response.data;
  }

  async createView(view: { name: string; shared: boolean; conditions: any[] }): Promise<any> {
    const response = await this.client.post('/views', view);
    return response.data.view;
  }

  async updateView(id: string, view: { name: string; conditions: any[] }): Promise<any> {
    const response = await this.client.put(`/views/${id}`, view);
    return response.data.view;
  }

  async deleteView(id: string): Promise<void> {
    await this.client.delete(`/views/${id}`);
  }

  // Billing (Settings > Billing)
  async getBilling(): Promise<any> {
    const response = await this.client.get('/org-billing');
    return response.data;
  }

  async startCheckout(
    plan: string,
    interval: 'month' | 'year'
  ): Promise<{ url?: string; changed?: boolean }> {
    const response = await this.client.post('/org-billing/checkout', { plan, interval });
    return response.data;
  }

  /** What one more (or one fewer) agent would cost - for the confirmation. */
  async seatPreview(change: 1 | -1 = 1): Promise<any> {
    const response = await this.client.get('/org-billing/seat-preview', { params: { change } });
    return response.data;
  }

  async openBillingPortal(): Promise<{ url: string }> {
    const response = await this.client.post('/org-billing/portal', {});
    return response.data;
  }

  /** The public price list (pricing and sign-up pages). */
  async getPlans(): Promise<{ plans: any[]; trialDays: number }> {
    const response = await this.client.get('/plans');
    return response.data;
  }

  /** A business signs up: its company and owner. The owner confirms their email to sign in. */
  /** Is a portal name free (sign-up, no login needed)? With company only, a suggestion. */
  async checkPortalName(params: { name?: string; company?: string }): Promise<any> {
    const response = await this.client.get('/company-registration/portal-name', { params });
    return response.data;
  }

  async registerCompany(data: {
    portalName?: string;
    companyName: string;
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
    adminPassword: string;
    subscriptionPlanId?: string;
    startTrial?: boolean;
  }): Promise<any> {
    const response = await this.client.post('/company-registration/register', data);
    return response.data;
  }

  async getAvailablePlans(): Promise<any> {
    const response = await this.client.get('/subscriptions/plans');
    return response.data;
  }

  async getUserSubscription(): Promise<any> {
    const response = await this.client.get('/subscriptions/current');
    return response.data;
  }

  async createSubscription(
    planId: string,
    options?: {
      startTrial?: boolean;
      paymentMethodId?: string;
    }
  ): Promise<any> {
    const response = await this.client.post('/subscriptions', {
      planId,
      ...options,
    });
    return response.data;
  }

  async upgradeSubscription(subscriptionId: string, newPlanId: string): Promise<any> {
    const response = await this.client.put(`/subscriptions/${subscriptionId}/upgrade`, {
      newPlanId,
    });
    return response.data;
  }

  async cancelSubscription(subscriptionId: string, cancelAtPeriodEnd?: boolean): Promise<any> {
    // The backend defines this as POST, not PUT.
    const response = await this.client.post(`/subscriptions/${subscriptionId}/cancel`, {
      cancelAtPeriodEnd,
    });
    return response.data;
  }

  // Admin endpoints
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.client.get('/admin/users', { params });
    return response.data;
  }

  async getUserDetails(userId: string): Promise<any> {
    const response = await this.client.get(`/admin/users/${userId}`);
    return response.data;
  }

  async updateUserRole(userId: string, role: string): Promise<any> {
    const response = await this.client.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<any> {
    const response = await this.client.put(`/admin/users/${userId}/status`, { isActive });
    return response.data;
  }

  async createUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
    teamId?: string;
  }): Promise<any> {
    const response = await this.client.post('/admin/users', userData);
    return response.data;
  }

  async updateUser(
    userId: string,
    userData: {
      firstName?: string;
      lastName?: string;
      email?: string;
      role?: string;
      isActive?: boolean;
      teamId?: string;
    }
  ): Promise<any> {
    const response = await this.client.put(`/admin/users/${userId}`, userData);
    return response.data;
  }

  async getRoleStats(): Promise<any> {
    const response = await this.client.get('/admin/stats/roles');
    return response.data;
  }

  // Usage alert endpoints
  async getUsageWarnings(): Promise<any> {
    const response = await this.client.get('/usage-alerts/warnings');
    return response.data;
  }

  async getDashboardWarnings(): Promise<any> {
    const response = await this.client.get('/usage-alerts/dashboard-warnings');
    return response.data;
  }

  async getTicketCreationWarning(): Promise<any> {
    const response = await this.client.get('/usage-alerts/ticket-creation-warning');
    return response.data;
  }

  async getUsageStats(): Promise<any> {
    const response = await this.client.get('/usage-alerts/usage-stats');
    return response.data;
  }

  // User deletion endpoints
  async checkUserTickets(userId: string): Promise<any> {
    const response = await this.client.get(`/admin/users/${userId}/assigned-tickets`);
    return response.data;
  }

  async permanentlyDeleteUser(userId: string): Promise<any> {
    const response = await this.client.delete(`/admin/users/${userId}/permanent`);
    return response.data;
  }

  async canCreateTicket(): Promise<any> {
    const response = await this.client.get('/usage-alerts/can-create-ticket');
    return response.data;
  }

  // Trial management endpoints
  async startTrial(
    planSlug: string,
    options?: {
      trialDays?: number;
      sendWelcomeEmail?: boolean;
      metadata?: Record<string, any>;
    }
  ): Promise<any> {
    const response = await this.client.post('/subscriptions/trial/start', {
      planSlug,
      ...options,
    });
    return response.data;
  }

  async convertTrial(
    subscriptionId: string,
    options: {
      paymentMethodId: string;
      stripeCustomerId?: string;
      stripeSubscriptionId?: string;
      sendWelcomeEmail?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.post(
      `/subscriptions/${subscriptionId}/trial/convert`,
      options
    );
    return response.data;
  }

  async cancelTrial(subscriptionId: string, reason?: string): Promise<any> {
    const response = await this.client.post(`/subscriptions/${subscriptionId}/trial/cancel`, {
      reason,
    });
    return response.data;
  }

  async getTrialStatus(subscriptionId: string): Promise<any> {
    const response = await this.client.get(`/subscriptions/${subscriptionId}/trial/status`);
    return response.data;
  }

  async extendTrial(subscriptionId: string, additionalDays: number, reason?: string): Promise<any> {
    const response = await this.client.post(`/subscriptions/${subscriptionId}/trial/extend`, {
      additionalDays,
      reason,
    });
    return response.data;
  }

  // Archival endpoints
  async getArchivalSuggestions(): Promise<any> {
    const response = await this.client.get('/tickets/archive/suggestions');
    return response.data;
  }

  async getAutoArchivalSuggestions(): Promise<any> {
    const response = await this.client.get('/tickets/archive/auto-suggestions');
    return response.data;
  }

  async archiveTicket(ticketId: string): Promise<any> {
    const response = await this.client.post(`/tickets/${ticketId}/archive`);
    return response.data;
  }

  async restoreTicket(ticketId: string): Promise<any> {
    const response = await this.client.post(`/tickets/${ticketId}/restore`);
    return response.data;
  }

  async bulkArchiveTickets(ticketIds: string[]): Promise<any> {
    const response = await this.client.post('/tickets/archive/bulk', { ticketIds });
    return response.data;
  }

  async getArchivableTickets(params?: { limit?: number; olderThanDays?: number }): Promise<any> {
    const response = await this.client.get('/tickets/archivable', { params });
    return response.data;
  }

  async searchArchivedTickets(params?: {
    query?: string;
    status?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
    page?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.client.get('/tickets/archived', { params });
    return response.data;
  }

  async getArchivalStats(): Promise<any> {
    const response = await this.client.get('/tickets/archive/stats');
    return response.data;
  }

  async getAutoArchivalConfig(): Promise<any> {
    const response = await this.client.get('/tickets/archive/auto-config');
    return response.data;
  }

  async updateAutoArchivalConfig(config: {
    enabled: boolean;
    daysAfterCompletion?: number;
    maxTicketsPerRun?: number;
  }): Promise<any> {
    const response = await this.client.post('/tickets/archive/auto-config', config);
    return response.data;
  }

  async triggerImmediateArchival(options?: {
    daysAfterCompletion?: number;
    maxTickets?: number;
  }): Promise<any> {
    const response = await this.client.post('/tickets/archive/trigger-immediate', options);
    return response.data;
  }

  async getAutomationStatus(): Promise<any> {
    const response = await this.client.get('/tickets/archive/automation-status');
    return response.data;
  }

  async runAutomatedArchival(): Promise<any> {
    const response = await this.client.post('/tickets/archive/run-automation');
    return response.data;
  }

  // Holiday List endpoints
  async getHolidayLists(): Promise<any> {
    const response = await this.client.get('/holiday-lists');
    return response.data;
  }

  async getHolidayList(id: number): Promise<any> {
    const response = await this.client.get(`/holiday-lists/${id}`);
    return response.data;
  }

  async createHolidayList(data: {
    holidayList: {
      name: string;
      description?: string;
      region?: string;
      is_active: boolean;
      is_default: boolean;
    };
    holidays: Array<{
      name: string;
      date: string;
      is_recurring: boolean;
      recurrence_pattern?: string;
      description?: string;
    }>;
  }): Promise<any> {
    const response = await this.client.post('/holiday-lists', data);
    return response.data;
  }

  async updateHolidayList(
    id: number,
    data: {
      holidayList: {
        name: string;
        description?: string;
        region?: string;
        is_active: boolean;
        is_default: boolean;
      };
      holidays: Array<{
        name: string;
        date: string;
        is_recurring: boolean;
        recurrence_pattern?: string;
        description?: string;
      }>;
    }
  ): Promise<any> {
    const response = await this.client.put(`/holiday-lists/${id}`, data);
    return response.data;
  }

  async deleteHolidayList(id: number): Promise<any> {
    const response = await this.client.delete(`/holiday-lists/${id}`);
    return response.data;
  }

  async getDefaultHolidayList(): Promise<any> {
    const response = await this.client.get('/holiday-lists/default/current');
    return response.data;
  }

  async checkHoliday(date: string): Promise<any> {
    const response = await this.client.get(`/holiday-lists/check/${date}`);
    return response.data;
  }

  // Business Hours endpoints
  async getBusinessHours(): Promise<any> {
    const response = await this.client.get('/business-hours');
    return response.data;
  }

  async createBusinessHours(data: any): Promise<any> {
    const response = await this.client.post('/business-hours', data);
    return response.data;
  }

  async updateBusinessHours(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/business-hours/${id}`, data);
    return response.data;
  }

  async deleteBusinessHours(id: number): Promise<any> {
    const response = await this.client.delete(`/business-hours/${id}`);
    return response.data;
  }

  async getDefaultBusinessHours(): Promise<any> {
    const response = await this.client.get('/business-hours/default/current');
    return response.data;
  }

  // Organization endpoints
  async getUserOrganizations(): Promise<any> {
    const response = await this.client.get('/orgs');
    return response.data;
  }

  async getDefaultOrganization(): Promise<any> {
    const response = await this.client.get('/orgs/default');
    return response.data;
  }

  async setDefaultOrganization(orgId: string): Promise<any> {
    const response = await this.client.post(`/orgs/${orgId}/set-default`);
    return response.data;
  }

  async getOrganization(orgId: string): Promise<any> {
    const response = await this.client.get(`/orgs/${orgId}`);
    return response.data;
  }

  // Company Profile endpoints
  async getCompanyProfile(): Promise<any> {
    const response = await this.client.get('/orgs/profile');
    return response.data;
  }

  async updateCompanyProfile(data: {
    name?: string;
    logoUrl?: string;
    websiteUrl?: string;
    primaryContactName?: string;
    primaryContactEmail?: string;
    primaryContactPhone?: string;
    mobilePhone?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
  }): Promise<any> {
    const response = await this.client.put('/orgs/profile', data);
    return response.data;
  }

  // Branding endpoints
  async getBranding(): Promise<any> {
    const response = await this.client.get('/company-registration/branding');
    return response.data;
  }

  async updateBranding(data: {
    logo?: string;
    favicon?: string;
    linkbackUrl?: string;
    companyName?: string;
    tagline?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    emailSignature?: string;
    autoInviteContacts?: boolean;
    portalBackground?: string;
    portalWelcomeTitle?: string;
    portalWelcomeText?: string;
  }): Promise<any> {
    const response = await this.client.put('/company-registration/branding', data);
    return response.data;
  }

  // Branded email to customers: an agent's own signature, and previews
  async getMySignature(): Promise<{ html: string }> {
    const response = await this.client.get('/email-branding/my-signature');
    return response.data;
  }

  async saveMySignature(html: string): Promise<{ html: string }> {
    const response = await this.client.put('/email-branding/my-signature', { html });
    return response.data;
  }

  async getEmailPreview(
    kind: 'welcome' | 'reply' | 'receipt' | 'update',
    signature?: string,
    mine?: boolean
  ): Promise<{ subject: string; html: string; from: string }> {
    const params: Record<string, string> = { kind };
    if (signature !== undefined) params['signature'] = signature;
    if (mine) params['mine'] = '1';
    const response = await this.client.get('/email-branding/preview', { params });
    return response.data;
  }

  // Department endpoints
  async getDepartments(): Promise<any> {
    const response = await this.client.get('/departments');
    return response.data;
  }

  async getDepartment(id: number): Promise<any> {
    const response = await this.client.get(`/departments/${id}`);
    return response.data;
  }

  async createDepartment(data: {
    name: string;
    description?: string;
    logo?: string;
    color?: string;
    is_active?: boolean;
    is_default?: boolean;
  }): Promise<any> {
    const response = await this.client.post('/departments', data);
    return response.data;
  }

  async updateDepartment(
    id: number,
    data: {
      name?: string;
      description?: string;
      logo?: string;
      color?: string;
      is_active?: boolean;
      is_default?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.put(`/departments/${id}`, data);
    return response.data;
  }

  async deleteDepartment(id: number): Promise<any> {
    const response = await this.client.delete(`/departments/${id}`);
    return response.data;
  }

  async addAgentToDepartment(
    departmentId: number,
    userId: string,
    role: 'member' | 'lead' | 'manager' = 'member'
  ): Promise<any> {
    const response = await this.client.post(`/departments/${departmentId}/agents`, {
      user_id: userId,
      role,
    });
    return response.data;
  }

  async removeAgentFromDepartment(departmentId: number, userId: string): Promise<any> {
    const response = await this.client.delete(`/departments/${departmentId}/agents/${userId}`);
    return response.data;
  }

  async getUserDepartments(): Promise<any> {
    const response = await this.client.get('/departments/user/my-departments');
    return response.data;
  }

  async getDepartmentTemplates(departmentId: number): Promise<any> {
    const response = await this.client.get(`/departments/${departmentId}/templates`);
    return response.data;
  }

  async createDepartmentTemplate(
    departmentId: number,
    data: {
      name: string;
      description?: string;
      template_fields: any;
      default_values?: any;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      category?: string;
      is_active?: boolean;
      is_default?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.post(`/departments/${departmentId}/templates`, data);
    return response.data;
  }

  async updateDepartmentTemplate(
    templateId: number,
    data: {
      name?: string;
      description?: string;
      template_fields?: any;
      default_values?: any;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      category?: string;
      is_active?: boolean;
      is_default?: boolean;
    }
  ): Promise<any> {
    const response = await this.client.put(`/departments/templates/${templateId}`, data);
    return response.data;
  }

  async deleteDepartmentTemplate(templateId: number): Promise<any> {
    const response = await this.client.delete(`/departments/templates/${templateId}`);
    return response.data;
  }

  // Customer Happiness endpoints
  async getCustomerHappinessSettings(): Promise<any> {
    const response = await this.client.get('/customer-happiness');
    return response.data;
  }

  async getCustomerHappinessSetting(id: number): Promise<any> {
    const response = await this.client.get(`/customer-happiness/${id}`);
    return response.data;
  }

  async createCustomerHappinessSetting(data: {
    name: string;
    description?: string;
    is_active?: boolean;
    is_default?: boolean;
    survey_config: any;
    trigger_conditions: any;
    email_template: any;
    delay_hours?: number;
    reminder_hours?: number;
    max_reminders?: number;
    thank_you_message?: string;
    follow_up_message?: string;
    low_rating_threshold?: number;
  }): Promise<any> {
    const response = await this.client.post('/customer-happiness', data);
    return response.data;
  }

  async updateCustomerHappinessSetting(
    id: number,
    data: {
      name?: string;
      description?: string;
      is_active?: boolean;
      is_default?: boolean;
      survey_config?: any;
      trigger_conditions?: any;
      email_template?: any;
      delay_hours?: number;
      reminder_hours?: number;
      max_reminders?: number;
      thank_you_message?: string;
      follow_up_message?: string;
      low_rating_threshold?: number;
    }
  ): Promise<any> {
    const response = await this.client.put(`/customer-happiness/${id}`, data);
    return response.data;
  }

  async deleteCustomerHappinessSetting(id: number): Promise<any> {
    const response = await this.client.delete(`/customer-happiness/${id}`);
    return response.data;
  }

  async getCustomerHappinessAnalytics(
    startDate: string,
    endDate: string,
    happinessSettingId?: number
  ): Promise<any> {
    const params: any = { start_date: startDate, end_date: endDate };
    if (happinessSettingId) params.happiness_setting_id = happinessSettingId;

    const response = await this.client.get('/customer-happiness/analytics/overview', { params });
    return response.data;
  }

  async getRecentCustomerFeedback(limit?: number, happinessSettingId?: number): Promise<any> {
    const params: any = {};
    if (limit) params.limit = limit;
    if (happinessSettingId) params.happiness_setting_id = happinessSettingId;

    const response = await this.client.get('/customer-happiness/feedback/recent', { params });
    return response.data;
  }

  // Public survey endpoints (no auth required)
  async getSurveyByToken(token: string): Promise<any> {
    const response = await this.client.get(`/customer-happiness/survey/${token}`);
    return response.data;
  }

  async submitSurveyResponse(
    token: string,
    data: {
      overall_rating: number;
      question_responses?: Record<string, any>;
      comments?: string;
    }
  ): Promise<any> {
    const response = await this.client.post(`/customer-happiness/survey/${token}/submit`, data);
    return response.data;
  }

  // Generic HTTP methods for flexibility
  async get(url: string, config?: any): Promise<any> {
    const response = await this.client.get(url, config);
    return response;
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.client.post(url, data, config);
    return response;
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.client.put(url, data, config);
    return response;
  }

  async delete(url: string, config?: any): Promise<any> {
    const response = await this.client.delete(url, config);
    return response;
  }

  // Organizational Roles
  async getOrganizationalRoles(): Promise<any> {
    const response = await this.client.get('/organizational-roles');
    return response.data;
  }

  async createOrganizationalRole(data: {
    name: string;
    description?: string;
    hierarchy_level: number;
  }): Promise<any> {
    const response = await this.client.post('/organizational-roles', data);
    return response.data;
  }

  async updateOrganizationalRole(
    id: number,
    data: { name?: string; description?: string; hierarchy_level?: number }
  ): Promise<any> {
    const response = await this.client.put(`/organizational-roles/${id}`, data);
    return response.data;
  }

  async deleteOrganizationalRole(id: number): Promise<any> {
    const response = await this.client.delete(`/organizational-roles/${id}`);
    return response.data;
  }

  // Products
  async getProducts(params?: { department_id?: string }): Promise<any> {
    const response = await this.client.get('/products', { params });
    return response.data;
  }

  async createProduct(data: {
    product_code: string;
    name: string;
    description?: string;
    department_id: string;
  }): Promise<any> {
    const response = await this.client.post('/products', data);
    return response.data;
  }

  async updateProduct(
    id: number,
    data: { product_code?: string; name?: string; description?: string; department_id?: string }
  ): Promise<any> {
    const response = await this.client.put(`/products/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: number): Promise<any> {
    const response = await this.client.delete(`/products/${id}`);
    return response.data;
  }

  // Custom modules (Settings > Modules) and their records
  async getModules(): Promise<{ id: string; key: string; name: string; singular: string }[]> {
    const response = await this.client.get('/modules');
    return Array.isArray(response.data?.modules) ? response.data.modules : [];
  }

  async createModule(data: { name: string; singular: string }): Promise<any> {
    const response = await this.client.post('/modules', data);
    return response.data.module;
  }

  async updateModule(key: string, data: { name: string; singular: string }): Promise<any> {
    const response = await this.client.put(`/modules/${key}`, data);
    return response.data.module;
  }

  async deleteModule(key: string): Promise<void> {
    await this.client.delete(`/modules/${key}`);
  }

  async listRecords(module: string, q = '', page = 1): Promise<any> {
    const response = await this.client.get(`/records/${module}`, { params: { q, page } });
    return response.data;
  }

  async createRecord(
    module: string,
    data: { values: Record<string, unknown>; customFieldValues?: Record<string, unknown> }
  ): Promise<any> {
    const response = await this.client.post(`/records/${module}`, data);
    return response.data.record;
  }

  async deleteRecord(module: string, id: string): Promise<void> {
    await this.client.delete(`/records/${module}/${id}`);
  }

  /** What links to a record, grouped by the lookup field. */
  async getRelated(module: string, id: string): Promise<any[]> {
    const response = await this.client.get(`/records/${module}/${id}/related`);
    return response.data.related;
  }

  /** Records to link to (q), or the names of linked ones (ids). */
  async lookup(
    module: string,
    params: { q?: string; ids?: string[] }
  ): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get(`/lookup/${module}`, {
      params: params.ids ? { ids: params.ids.join(',') } : { q: params.q || '' },
    });
    return response.data.records;
  }

  // Accounts and contacts through their layouts
  async getRecord(module: string, id: string): Promise<any> {
    const response = await this.client.get(`/records/${module}/${id}`);
    return response.data.record;
  }

  /**
   * An account's or contact's page: overview (counts, channels, handling
   * times), tickets (?filter=all|open|on_hold), people (contacts / accounts),
   * history and attachments.
   */
  async getRecordPart(
    module: 'accounts' | 'contacts',
    id: string,
    part: 'overview' | 'tickets' | 'people' | 'history' | 'attachments',
    params: Record<string, string | number> = {}
  ): Promise<any> {
    const response = await this.client.get(`/records/${module}/${id}/${part}`, { params });
    return response.data;
  }

  async updateRecord(
    module: string,
    id: string,
    changes: { values?: Record<string, unknown>; customFieldValues?: Record<string, unknown> }
  ): Promise<any> {
    const response = await this.client.put(`/records/${module}/${id}`, changes);
    return response.data.record;
  }

  // Fields and layouts (Settings > Ticket Layout), per module ('tickets')
  async getFields(module: string): Promise<any> {
    const response = await this.client.get(`/fields/${module}`);
    return response.data;
  }

  async createField(module: string, field: any): Promise<any> {
    const response = await this.client.post(`/fields/${module}`, field);
    return response.data;
  }

  async updateField(module: string, id: string, field: any): Promise<any> {
    const response = await this.client.put(`/fields/${module}/${id}`, field);
    return response.data;
  }

  async deleteField(module: string, id: string): Promise<any> {
    const response = await this.client.delete(`/fields/${module}/${id}`);
    return response.data;
  }

  async saveFieldLayout(module: string, sections: any[]): Promise<any> {
    const response = await this.client.put(`/fields/${module}/layout`, { sections });
    return response.data;
  }

  // Macros: saved replies + ticket changes
  async getMacros(): Promise<any> {
    const response = await this.client.get('/macros');
    return response.data;
  }

  async createMacro(macro: any): Promise<any> {
    const response = await this.client.post('/macros', macro);
    return response.data;
  }

  async updateMacro(id: string, macro: any): Promise<any> {
    const response = await this.client.put(`/macros/${id}`, macro);
    return response.data;
  }

  async deleteMacro(id: string): Promise<any> {
    const response = await this.client.delete(`/macros/${id}`);
    return response.data;
  }

  /** Makes the macro's changes; returns { ticket, reply, changed } (reply not sent). */
  async applyMacro(macroId: string, ticketId: string): Promise<any> {
    const response = await this.client.post(`/macros/${macroId}/apply`, { ticketId });
    return response.data;
  }

  // Assignment rules (Settings > Assignment Rules)
  async getAssignmentRules(): Promise<any> {
    const response = await this.client.get('/assignment-rules');
    return response.data;
  }

  async createAssignmentRule(rule: any): Promise<any> {
    const response = await this.client.post('/assignment-rules', rule);
    return response.data;
  }

  async updateAssignmentRule(id: string, rule: any): Promise<any> {
    const response = await this.client.put(`/assignment-rules/${id}`, rule);
    return response.data;
  }

  async deleteAssignmentRule(id: string): Promise<any> {
    const response = await this.client.delete(`/assignment-rules/${id}`);
    return response.data;
  }

  async reorderAssignmentRules(ruleIds: string[]): Promise<any> {
    const response = await this.client.put('/assignment-rules/order', { ruleIds });
    return response.data;
  }

  // Gamification
  async getTrophies(): Promise<any> {
    const response = await this.client.get('/gamification/trophies');
    return response.data;
  }

  async getBadges(): Promise<any> {
    const response = await this.client.get('/gamification/badges');
    return response.data;
  }

  async getLeaderboard(): Promise<any> {
    const response = await this.client.get('/gamification/leaderboard');
    return response.data;
  }

  async createTrophy(data: any): Promise<any> {
    const response = await this.client.post('/gamification/trophies', data);
    return response.data;
  }

  async updateTrophy(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/gamification/trophies/${id}`, data);
    return response.data;
  }

  async createBadge(data: any): Promise<any> {
    const response = await this.client.post('/gamification/badges', data);
    return response.data;
  }

  async updateBadge(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/gamification/badges/${id}`, data);
    return response.data;
  }

  // User Profiles
  async getUserProfiles(): Promise<any> {
    const response = await this.client.get('/user-profiles');
    return response.data;
  }

  // Profile Field Configuration
  async getProfileFields(): Promise<any> {
    const response = await this.client.get('/profile-fields');
    return response.data;
  }

  async updateProfileFields(fields: any[]): Promise<any> {
    const response = await this.client.put('/profile-fields', { fields });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;

// Export as 'api' for backward compatibility
export const api = apiService;
